<template>
  <v-data-table
    ref="dataTable"
    v-model="currentSelected"
    v-bind="{...$attrs, showSelect, showExpand}"
    :locale="$i18n.locale"
    :headers="filteredHeaders"
    :items="items"
    :item-key="itemKey"
    :page.sync="currentPage"
    :items-per-page.sync="currentLimit"
    :server-items-length="total"
    :sort-by.sync="currentSortKey"
    :sort-desc.sync="currentSortDesc"
    :loading="loading"
    :search="delayedSearch"
    :expanded.sync="expanded"
  >
    <template v-slot:item="props">
      <tr
        :key="props.index"
        :class="{
          'selected-row': props.isSelected && selectRow, 'dark': (props.index % 2 !== 0),
          'hovered': currentHovered === props.index
        }"
        @mouseover="currentHovered = props.index"
        @mouseleave="currentHovered = -1"
        @click="() => {
          $emit('click-row', props);
        }"
      >
        <td v-if="showSelect">
          <v-checkbox
            v-model="props.isSelected"
            class="mt-0 pt-0"
            :disabled="noCheckSelect"
            primary
            hide-details
            @click.stop="e => {
              props.select(!props.isSelected);
            }"
          />
        </td>

        <td v-for="(field, i) in filteredHeaders" :key="`${props.index.toString()}-${i}`">
          <slot :name="`item.${slotFieldName(field)}`" v-bind="props">
            <v-icon v-if="field.inactive && props.item.inactive === true" color="error">
              mdi-cancel
            </v-icon>

            <span v-else-if="_.isBoolean(fieldValue(props.item, field.value))">
              <v-icon v-if="fieldValue(props.item, field.value) === true">
                mdi-check-bold
              </v-icon>
            </span>

            <span v-else-if="valueIsEmptyArray(fieldValue(props.item, field.value))" />

            <timeago
              v-else-if="field.timeago"
              :datetime="fieldValue(props.item, field.value)"
              :auto-update="true"
              :locale="$i18n.locale"
            />

            <v-tooltip v-else-if="field.gravatar && !field.noTooltip" top>
              <template v-slot:activator="{ on }">
                <v-avatar class="avatar" v-on="on">
                  <v-gravatar
                    class="avatar"
                    :email="fieldValue(props.item, field.value)"
                    :style="getBorderStyle(props.item.updatedById)"
                  />
                </v-avatar>
              </template>
              <span>{{ field.gravatarTooltip.call(this, props.item, field) }}</span>
            </v-tooltip>

            <v-avatar v-else-if="field.gravatar" class="avatar">
              <v-gravatar
                :email="fieldValue(props.item, field.value)"
                :style="getBorderStyle(props.item._id)"
              />
            </v-avatar>

            <v-btn
              v-else-if="field.value === 'data-table-expand'"
              icon
              @click.stop="() => {
                if (showExpand) {
                  $emit('click-expand', props);
                  $refs.dataTable.expand(props.item, !props.isExpanded);
                }
              }"
            >
              <v-icon>
                {{ `${props.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'}` }}
              </v-icon>
            </v-btn>

            <v-menu
              v-else-if="field.value === 'data-table-menu'"
              offset-y
              left
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="grey"
                  icon
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="(item, index) in field.items"
                  :key="index"
                  link
                  @click="$emit('menu-click', item, props)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>

            <span v-else>{{ fieldValue(props.item, field.value) }}</span>
          </slot>
        </td>
      </tr>
    </template>

    <template v-slot:expanded-item="props">
      <td :colspan="props.headers.length">
        <slot name="expanded-item" v-bind="props" />
      </td>
    </template>

    <slot />
  </v-data-table>
</template>

<script>
import TableMixin from '../../mixins/table';
import StatesMixin from '../../mixins/states';
import AvatarMixin from '../../mixins/avatar';

export default {
  mixins: [
    TableMixin,
    StatesMixin,
    AvatarMixin,
  ],

  props: {
    // Don't clear selections when menu closes
    retainSelection: Boolean,

    // Colorize selected rows
    selectRow: Boolean,

    // disables select checkbox
    noCheckSelect: Boolean,

    showSelect: Boolean,
    showExpand: Boolean,
  },

  data() {
    return {
      headerState: {},
      expanded: [],
    };
  },

  computed: {
    filteredHeaders() {
      return _.filter(this.headers, (hdr) => !_.get(this.headerState, [hdr.value.toString(), 'hidden']));
    },
  },

  watch: {
    items() {
      this.processHeaders();
    },
  },

  mounted() {
    this.processHeaders();
  },

  methods: {
    fieldValue(item, value) {
      if (_.isFunction(value)) {
        return value.call(this, item);
      }
      return _.get(item, value);
    },

    /**
     * Calculates all headers states.
     */
    processHeaders() {
      _.each(this.headers, (header) => {
        this.processStates(
          header,
          ['hidden'],
          header.value.toString(),
          'headerState',
        );
      });
    },

    /**
     * Returns true if the value is an array and is empty.
     *
     * @param {any} value Value to check.
     *
     * @returns {boolean}
     */
    valueIsEmptyArray(value) {
      return _.isArray(value) && _.isEmpty(value);
    },

    selectById(_id) {
      const entity = _.find(this.items, { _id });
      if (entity) {
        this.currentSelected = [entity];
        this.$emit('click-row', entity);
      }
    },

    /**
     * Clear selections.
     */
    clearSelection() {
      if (!this.retainSelection) {
        this.$set(this, 'currentSelected', []);
      }
    },

    slotFieldName(field) {
      let fn = _.get(field, 'value', '').toString();
      if (fn.indexOf('.') !== -1) {
        fn = _.camelCase(fn.replace('.', '_'));
      }
      return fn;
    },
  },
};
</script>

<style scoped>
.avatar {
  width: 32px !important;
  height: 32px !important;
}
.selected-row {
  background-color: #bbddbd;
}
.dark {
  background-color: #fff;
}
.avatar {
  margin: 0em .5em !important;
  width: 24px !important;
  min-width: 24px !important;
  height: 24px !important;
}
</style>
